import type { MindMapDocument } from '@/model/document';
import type { MindMapNode } from '@/model/types';
import type { ClipboardPayload } from './types';
import { serializeSubtree, deserializeSubtree } from './serialize';
import { isUrl, extractUrls } from './urlDetect';
import { requestUploadUrl, uploadFile } from '@/api/attachments';

const CUSTOM_MIME_TYPE = 'application/x-ideatwig-nodes';

/** Set of element tag names that should not have clipboard events intercepted. */
const FORM_ELEMENTS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export class ClipboardHandler {
  constructor(
    private getSelectedNodeId: () => string | undefined,
    private getMapDoc: () => MindMapDocument,
    private getMapId: () => string | null,
    private getNodes: () => Map<string, MindMapNode>,
    private isEditing: () => boolean,
    private onUploadStart: (nodeId: string) => void,
    private onUploadComplete: (nodeId: string) => void,
    private onUploadError: (nodeId: string, error: string) => void,
  ) {}

  // ── Paste ─────────────────────────────────────────────────────

  handlePaste = (e: ClipboardEvent): void => {
    // Don't intercept paste in form elements
    if (this.isFormElement(e.target)) return;

    // Don't intercept paste when editing a node inline -- let the browser handle it
    if (this.isEditing()) return;

    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    // 1. Check for custom ideatwig node data
    const customData = clipboardData.getData(CUSTOM_MIME_TYPE);
    if (customData) {
      e.preventDefault();
      this.pasteNodes(customData);
      return;
    }

    // 2. Check for files (images, etc.)
    if (clipboardData.files.length > 0) {
      e.preventDefault();
      this.pasteFiles(clipboardData.files);
      return;
    }

    // 3. Check for URL in plain text
    const text = clipboardData.getData('text/plain');
    if (text && isUrl(text.trim())) {
      e.preventDefault();
      this.pasteUrl(text.trim());
      return;
    }

    // 4. Plain text -- set as node text if a node is selected
    if (text) {
      e.preventDefault();
      this.pasteText(text);
      return;
    }
  };

  // ── Copy ──────────────────────────────────────────────────────

  handleCopy = (e: ClipboardEvent): void => {
    if (this.isFormElement(e.target)) return;
    if (this.isEditing()) return;

    const nodeId = this.getSelectedNodeId();
    if (!nodeId) return;

    e.preventDefault();
    this.copyNode(nodeId, e.clipboardData);
  };

  // ── Cut ───────────────────────────────────────────────────────

  handleCut = (e: ClipboardEvent): void => {
    if (this.isFormElement(e.target)) return;
    if (this.isEditing()) return;

    const nodeId = this.getSelectedNodeId();
    if (!nodeId) return;

    const mapDoc = this.getMapDoc();
    const meta = mapDoc.getMeta();

    // Cannot cut the root node
    if (nodeId === meta.rootNodeId) return;

    e.preventDefault();
    this.copyNode(nodeId, e.clipboardData);
    mapDoc.deleteNode(nodeId);
  };

  // ── Drop ──────────────────────────────────────────────────────

  handleDrop = (e: DragEvent): void => {
    const dataTransfer = e.dataTransfer;
    if (!dataTransfer || dataTransfer.files.length === 0) return;

    e.preventDefault();
    e.stopPropagation();

    const nodeId = this.getSelectedNodeId();
    if (!nodeId) return;

    this.uploadFiles(nodeId, dataTransfer.files);
  };

  handleDragOver = (e: DragEvent): void => {
    // Allow drops by preventing default
    if (e.dataTransfer?.types.includes('Files')) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // ── Attach / Detach ───────────────────────────────────────────

  /**
   * Attaches clipboard and drag-drop listeners to the given element.
   * Returns a detach function that removes all listeners.
   */
  attach(element: HTMLElement): () => void {
    // Clipboard events on window (they bubble up from the focused element)
    window.addEventListener('paste', this.handlePaste);
    window.addEventListener('copy', this.handleCopy);
    window.addEventListener('cut', this.handleCut);

    // Drag/drop on the provided element (typically the SVG canvas container)
    element.addEventListener('drop', this.handleDrop);
    element.addEventListener('dragover', this.handleDragOver);

    return () => {
      window.removeEventListener('paste', this.handlePaste);
      window.removeEventListener('copy', this.handleCopy);
      window.removeEventListener('cut', this.handleCut);
      element.removeEventListener('drop', this.handleDrop);
      element.removeEventListener('dragover', this.handleDragOver);
    };
  }

  // ── Private Helpers ───────────────────────────────────────────

  private isFormElement(target: EventTarget | null): boolean {
    if (!target) return false;
    const el = target as HTMLElement;
    return FORM_ELEMENTS.has(el.tagName);
  }

  private copyNode(nodeId: string, clipboardData: DataTransfer | null): void {
    if (!clipboardData) return;

    const nodes = this.getNodes();
    const serialized = serializeSubtree(nodeId, nodes);
    if (!serialized) return;

    const payload: ClipboardPayload = {
      type: 'ideatwig-nodes',
      version: 1,
      nodes: [serialized],
    };

    clipboardData.setData(CUSTOM_MIME_TYPE, JSON.stringify(payload));
    clipboardData.setData('text/plain', serialized.text);
  }

  private pasteNodes(customData: string): void {
    const selectedId = this.getSelectedNodeId();
    if (!selectedId) return;

    try {
      const payload = JSON.parse(customData) as ClipboardPayload;
      if (payload.type !== 'ideatwig-nodes' || !Array.isArray(payload.nodes)) return;

      const mapDoc = this.getMapDoc();
      for (const serialized of payload.nodes) {
        deserializeSubtree(serialized, mapDoc, selectedId);
      }
    } catch {
      // Invalid JSON or payload format -- silently ignore
    }
  }

  private pasteFiles(files: FileList): void {
    const nodeId = this.getSelectedNodeId();
    if (!nodeId) return;

    this.uploadFiles(nodeId, files);
  }

  private pasteUrl(url: string): void {
    const nodeId = this.getSelectedNodeId();
    if (!nodeId) return;

    const mapDoc = this.getMapDoc();
    mapDoc.addNodeLink(nodeId, url);
  }

  private pasteText(text: string): void {
    const nodeId = this.getSelectedNodeId();
    if (!nodeId) return;

    const mapDoc = this.getMapDoc();

    // If the text contains URLs, extract and add them as links
    const urls = extractUrls(text);
    if (urls.length > 0) {
      for (const url of urls) {
        mapDoc.addNodeLink(nodeId, url);
      }
      // If the text is purely URLs, don't change the node text
      const nonUrlText = text;
      for (const url of urls) {
        // Remove URLs from text to see if there's remaining content
        text = text.replace(url, '').trim();
      }
      if (text.length === 0) return;
      // Restore original text (has non-URL content too)
      text = nonUrlText;
    }

    // Set the text on the selected node
    mapDoc.updateNodeText(nodeId, text);
  }

  private uploadFiles(nodeId: string, files: FileList): void {
    const mapId = this.getMapId();
    if (!mapId) return;

    const mapDoc = this.getMapDoc();

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) continue;

      this.onUploadStart(nodeId);

      void (async () => {
        try {
          const { attachmentId, s3Key, uploadUrl } = await requestUploadUrl(
            mapId,
            file.name,
            file.type,
            file.size,
          );
          await uploadFile(uploadUrl, file);

          mapDoc.addNodeAttachment(nodeId, {
            id: attachmentId,
            fileName: file.name,
            mimeType: file.type,
            s3Key,
            sizeBytes: file.size,
            uploadedAt: new Date().toISOString(),
          });

          this.onUploadComplete(nodeId);
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Upload failed';
          this.onUploadError(nodeId, message);
        }
      })();
    }
  }
}
